import { useState } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import "./SalesReport.css";

export default function SalesReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  // 🔹 Generar reporte
  const handleGenerateReport = async () => {
    if (!fromDate || !toDate) {
      return Swal.fire("Atención", "Debes seleccionar ambas fechas", "warning");
    }

    if (new Date(fromDate) > new Date(toDate)) {
      return Swal.fire("Error", "La fecha inicial no puede ser mayor a la fecha final", "error");
    }

    setLoading(true);
    try {
      const response = await api.get("/reports/sales", {
        params: { from: fromDate, to: toDate }
      });

      setReportData(response.data);
      
      Swal.fire({
        icon: "success",
        title: "Reporte Generado",
        text: "El reporte se generó correctamente",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error("Error generando reporte:", error);
      Swal.fire("Error", "No se pudo generar el reporte", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Limpiar reporte
  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setReportData(null);
  };

  // 🔹 Exportar a Excel
  const handleExportExcel = () => {
    if (!reportData) return;

    const workbook = XLSX.utils.book_new();

    // Hoja 1: Resumen
    const resumenData = [
      ["REPORTE DE VENTAS"],
      ["Período:", `${reportData.periodo.desde} al ${reportData.periodo.hasta}`],
      [],
      ["RESUMEN GENERAL"],
      ["Total Ventas", `$${reportData.resumen.total_ventas.toFixed(2)}`],
      ["Total Facturas", reportData.resumen.total_facturas],
      ["Total IVA", `$${reportData.resumen.total_iva.toFixed(2)}`],
      ["Subtotal", `$${reportData.resumen.total_subtotal.toFixed(2)}`],
      ["Promedio por Venta", `$${reportData.resumen.promedio_venta.toFixed(2)}`],
    ];
    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(workbook, wsResumen, "Resumen");

    // Hoja 2: Ventas por Día
    const ventasPorDia = reportData.ventas_por_dia.map(day => ({
      Fecha: day.fecha,
      "Total Facturas": day.total_facturas,
      Subtotal: day.subtotal,
      IVA: day.iva,
      Total: day.total
    }));
    const wsVentas = XLSX.utils.json_to_sheet(ventasPorDia);
    XLSX.utils.book_append_sheet(workbook, wsVentas, "Ventas por Día");

    // Hoja 3: Top Productos
    const topProductos = reportData.top_productos.map(prod => ({
      Código: prod.codigo,
      Producto: prod.nombre,
      "Cantidad Vendida": prod.cantidad_vendida,
      "Total Vendido": prod.total_vendido
    }));
    const wsProductos = XLSX.utils.json_to_sheet(topProductos);
    XLSX.utils.book_append_sheet(workbook, wsProductos, "Top Productos");

    // Descargar
    const fileName = `Reporte_Ventas_${fromDate}_al_${toDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: "success",
      title: "Excel Descargado",
      timer: 2000,
      showConfirmButton: false
    });
  };

  // 🔹 Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-SV', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  return (
    <div className="sales-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">📊 Reporte de Ventas</h2>
        </div>

        <div className="card-body">
          {/* Filtros */}
          <div className="filters-section">
            <div className="filters-row">
              <div className="form-group">
                <label className="form-label">📅 Fecha Inicio</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">📅 Fecha Fin</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="filters-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  {loading ? "⏳ Generando..." : "🔍 Generar Reporte"}
                </button>
                <button className="btn btn-secondary" onClick={handleClear}>
                  🔄 Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center mt-4">
              <div className="spinner"></div>
              <p className="mt-2">Generando reporte...</p>
            </div>
          )}

          {/* Resultados */}
          {reportData && !loading && (
            <div className="report-results">
              {/* Botón Exportar */}
              <div className="export-section">
                <button className="btn btn-success" onClick={handleExportExcel}>
                  📊 Exportar a Excel
                </button>
              </div>

              {/* Resumen General */}
              <div className="summary-section">
                <h3 className="section-title">📈 Resumen General</h3>
                <div className="kpi-cards">
                  <div className="kpi-card">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Ventas</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_ventas)}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">📄</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Facturas</div>
                      <div className="kpi-value">{reportData.resumen.total_facturas}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">💵</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total IVA</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_iva)}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">📊</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Promedio por Venta</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.promedio_venta)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ventas por Día */}
              <div className="sales-by-day-section">
                <h3 className="section-title">📅 Ventas por Día</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Facturas</th>
                        <th>Subtotal</th>
                        <th>IVA</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.ventas_por_dia.map((day, index) => (
                        <tr key={index}>
                          <td>{new Date(day.fecha).toLocaleDateString('es-ES')}</td>
                          <td className="text-center">{day.total_facturas}</td>
                          <td className="text-right">{formatCurrency(day.subtotal)}</td>
                          <td className="text-right">{formatCurrency(day.iva)}</td>
                          <td className="text-right font-bold">{formatCurrency(day.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="total-row">
                        <td><strong>TOTAL</strong></td>
                        <td className="text-center"><strong>{reportData.resumen.total_facturas}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_subtotal)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_iva)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_ventas)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Top Productos */}
              <div className="top-products-section">
                <h3 className="section-title">🏆 Top 10 Productos Más Vendidos</h3>
                
                {/* Gráfico */}
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={reportData.top_productos}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="nombre" type="category" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="cantidad_vendida" fill="#8884d8" name="Cantidad Vendida" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Tabla de productos */}
                <div className="table-container mt-3">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Código</th>
                        <th>Producto</th>
                        <th>Cantidad Vendida</th>
                        <th>Total Vendido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.top_productos.map((product, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{product.codigo}</td>
                          <td>{product.nombre}</td>
                          <td className="text-center">{product.cantidad_vendida}</td>
                          <td className="text-right">{formatCurrency(product.total_vendido)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Sin datos */}
          {!reportData && !loading && (
            <div className="no-data">
              <p>📭 Selecciona un rango de fechas y genera el reporte</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}